import { LineChartScalesConfig, GroupByOption, MetricDatum, SortByOption } from '../../../types';
import {
  formatNumber,
  calculateYAxisWidth,
  calculateXAxisLabelSpacing,
  CHAR_WIDTH_PX,
  MINIMUM_TICK_WIDTH_STRAIGHT,
  MINIMUM_TICK_WIDTH_ROTATED,
  X_AXIS_LABEL_ANGLE_STRAIGHT,
  X_AXIS_LABEL_ANGLE_ROTATED,
} from '../../../utils/chartUtils';

/**
 * Sorts line chart data by x-axis values
 * This is crucial for line charts to display trends correctly, especially with date data
 *
 * Note: ISO 8601 date strings (e.g., "2011-01-01") sort correctly using lexicographic comparison
 * without needing to parse as Date objects, since the format is naturally sortable (year-month-day).
 */
const sortDataByXAxis = (data: MetricDatum[], xAxisKeys: string[], sortBy?: SortByOption[]): MetricDatum[] => {
  if (!data || data.length === 0) {
    return data;
  }

  const xAxisKey = xAxisKeys[0];

  // Determine sort configuration
  let sortKey = xAxisKey;
  let sortMethod: 'asc' | 'desc' = 'asc';

  if (sortBy && sortBy.length > 0) {
    // Use explicit sort configuration if provided
    sortKey = sortBy[0].key;
    sortMethod = sortBy[0].method;
  }

  // Create a copy to avoid mutating the original array
  const sortedData = [...data];

  sortedData.sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];

    // Handle null/undefined values - push them to the end
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    let comparison = 0;

    // Use numeric comparison for numbers, string comparison for everything else
    // String comparison works correctly for ISO 8601 dates (e.g., "2011-01-01")
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      comparison = aValue - bValue;
    } else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    // Apply sort direction
    return sortMethod === 'desc' ? -comparison : comparison;
  });

  return sortedData;
};

const prepareGroupedChart = (
  data: MetricDatum[] | undefined,
  groupBy: GroupByOption[],
  xAxisKeys: string[],
  yAxisKeys: string[],
  yAxisLabels: string[],
) => {
  if (!data || data.length === 0) {
    return {
      data: [],
      yAxisKeys,
      yAxisLabels,
    };
  }

  const groupByKeys = groupBy.map((gb) => gb.key);

  // For line charts with grouping, the backend has already aggregated the data
  // We need to transform it so each group becomes a separate line series

  // First, collect all unique group values
  const uniqueGroupValues = new Set<string>();
  data.forEach((datum) => {
    const groupValue = groupByKeys
      .map((gbKey) => datum[gbKey])
      .filter(Boolean)
      .join(' - ');
    if (groupValue) {
      uniqueGroupValues.add(groupValue);
    }
  });

  // Create a map of x-axis values to their data points
  const transformedData: Record<string, Record<string, any>> = {};

  data.forEach((datum) => {
    // Get x-axis value for this data point
    const xAxisValue = datum[xAxisKeys[0]];
    const xAxisKey = String(xAxisValue);

    if (!transformedData[xAxisKey]) {
      transformedData[xAxisKey] = {};
      // Set the x-axis value
      transformedData[xAxisKey][xAxisKeys[0]] = xAxisValue;
    }

    // Get the group value for this data point
    const groupValue = groupByKeys
      .map((gbKey) => datum[gbKey])
      .filter(Boolean)
      .join(' - ');

    // For each y-axis field, create a series for each group
    yAxisKeys.forEach((yKey) => {
      const yValue = datum[yKey];
      if (yValue !== undefined) {
        // Create a unique key for this group-metric combination
        const seriesKey = groupValue ? `${yKey} - ${groupValue}` : yKey;
        transformedData[xAxisKey][seriesKey] = yValue;
      }
    });
  });

  // Generate new y-axis keys and labels for each group
  const newYAxisKeys: string[] = [];
  const newYAxisLabels: string[] = [];

  if (uniqueGroupValues.size > 0) {
    // For each y-axis field, create series for each group
    yAxisKeys.forEach((yKey, index) => {
      uniqueGroupValues.forEach((groupValue) => {
        const seriesKey = `${yKey} - ${groupValue}`;
        const seriesLabel = `${yAxisLabels[index]} - ${groupValue}`;
        newYAxisKeys.push(seriesKey);
        newYAxisLabels.push(seriesLabel);
      });
    });
  } else {
    // No grouping, use original keys
    newYAxisKeys.push(...yAxisKeys);
    newYAxisLabels.push(...yAxisLabels);
  }

  return {
    data: Object.values(transformedData) as MetricDatum[],
    yAxisKeys: newYAxisKeys,
    yAxisLabels: newYAxisLabels,
  };
};

type LineChartOptions = {
  data: MetricDatum[];
  groupBy?: GroupByOption[];
  scales?: LineChartScalesConfig;
  sortBy?: SortByOption[];
  xAxisKeys: string[];
  xAxisLabels?: string[];
  yAxisKeys: string[];
  yAxisLabels: string[];
  angleLabels?: boolean;
};

export const useLineChart = ({
  data,
  groupBy,
  scales,
  sortBy,
  xAxisKeys,
  xAxisLabels,
  yAxisKeys,
  yAxisLabels,
  angleLabels,
}: LineChartOptions) => {
  if (!yAxisKeys) {
    return {
      data,
      formatValue: () => '',
      hasMultipleSeries: false,
      xAxisKeys,
      xAxisLabels: xAxisLabels || [],
      xAxisLabelSpacing: () => 0,
      yAxisKeys,
      yAxisLabels,
      yAxisWidth: 0,
    };
  }

  const hasMultipleSeries = yAxisKeys.length > 1;
  const formatValue = (value: any) =>
    formatNumber({
      value,
      format: scales?.y?.tickFormat,
    });
  const yAxisWidth = calculateYAxisWidth(
    data,
    yAxisKeys,
    formatValue,
    CHAR_WIDTH_PX,
    angleLabels ? MINIMUM_TICK_WIDTH_ROTATED : MINIMUM_TICK_WIDTH_STRAIGHT,
  );
  const xAxisLabelSpacing = (key: string | number) =>
    calculateXAxisLabelSpacing(
      data,
      key,
      CHAR_WIDTH_PX,
      angleLabels ? X_AXIS_LABEL_ANGLE_ROTATED : X_AXIS_LABEL_ANGLE_STRAIGHT,
    );

  if (groupBy?.length) {
    const groupedChart = prepareGroupedChart(data, groupBy, xAxisKeys, yAxisKeys, yAxisLabels);
    return {
      ...groupedChart,
      data: sortDataByXAxis(groupedChart.data, xAxisKeys, sortBy),
      formatValue,
      hasMultipleSeries,
      xAxisKeys,
      xAxisLabels: xAxisLabels || [],
      xAxisLabelSpacing,
      yAxisWidth,
    };
  }

  return {
    data: sortDataByXAxis(data, xAxisKeys, sortBy),
    formatValue,
    hasMultipleSeries,
    xAxisKeys,
    xAxisLabels: xAxisLabels || [],
    xAxisLabelSpacing,
    yAxisKeys,
    yAxisLabels,
    yAxisWidth,
  };
};
